import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import { CookieBanner } from "./components/CookieBanner";
import { InstallPrompt } from "./components/InstallPrompt";
import { SignupPopup } from "./components/SignupPopup";
import { Analytics } from "./components/Analytics";

// Lazy-load all non-home routes to keep initial bundle minimal.
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy.tsx"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn.tsx"));
const SubscribeCheckout = lazy(() => import("./pages/SubscribeCheckout.tsx"));
const Account = lazy(() => import("./pages/Account.tsx"));
const SubscriptionsManage = lazy(() => import("./pages/SubscriptionsManage.tsx"));
const OrderTracking = lazy(() => import("./pages/OrderTracking.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Journal = lazy(() => import("./pages/Journal.tsx"));
const JournalPost = lazy(() => import("./pages/JournalPost.tsx"));
const JournalTopic = lazy(() => import("./pages/JournalTopic.tsx"));
const Author = lazy(() => import("./pages/Author.tsx"));
const Glossary = lazy(() => import("./pages/Glossary.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const Wholesale = lazy(() => import("./pages/Wholesale.tsx"));
const WholesaleAccount = lazy(() => import("./pages/WholesaleAccount.tsx"));
const Watch = lazy(() => import("./pages/Watch.tsx"));
const KashmiriChai = lazy(() => import("./pages/KashmiriChai.tsx"));
const SaffronGuide = lazy(() => import("./pages/SaffronGuide.tsx"));
const KahwaGuide = lazy(() => import("./pages/KahwaGuide.tsx"));
const Compare = lazy(() => import("./pages/Compare.tsx"));
const WhySaffron = lazy(() => import("./pages/WhySaffron.tsx"));

const queryClient = new QueryClient();

const JournalRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/journal/, "/thekesarkitchen") + search + hash} replace />;
};

const OldKitchenRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/thekitchen/, "/thekesarkitchen") + search + hash} replace />;
};

// Map of legacy WooCommerce-era product slugs → current handles.
const LEGACY_PRODUCT_SLUGS: Record<string, string> = {
  "param-kesar-kashmir-super-mongra-saffron-1g-of-saffron": "kashmiri-saffron",
  "param-kesar-kashmir-super-mongra-saffron-5g-of-saffron": "kashmiri-saffron",
  "param-kesar-kashmir-super-mongra-saffron-10g-of-saffron": "kashmiri-saffron",
  "param-kesar-kashmir-super-mongra-saffron-100g-of-saffron": "kashmiri-saffron",
  "param-kesar-kashmir-super-mongra-saffron": "kashmiri-saffron",
  "param-kesar-saffron-kahwa-100-grams-detox-herbal-green-tea": "kashmiri-kahwa-tea",
  "param-kesar-saffron-kahwa": "kashmiri-kahwa-tea",
  "param-kesar-noon-chai": "kashmiri-noon-chai",
  "param-kesar-pink-tea": "kashmiri-noon-chai",
};

// Strip leading locale prefixes (e.g. /es/, /fr/) that came from old WP multilingual setup,
// then redirect legacy product slugs and trailing slashes to current handles.
const LegacyUrlRedirect = () => {
  const { pathname, search } = useLocation();
  // Drop locale prefix
  let clean = pathname.replace(/^\/(es|fr|de|it|pt|hi|zh|ja|ar|ru)(\/|$)/i, "/");
  // Drop trailing slash (except root)
  if (clean.length > 1 && clean.endsWith("/")) clean = clean.slice(0, -1);
  // Map legacy product slugs
  const m = clean.match(/^\/product\/([^/?#]+)$/);
  if (m && LEGACY_PRODUCT_SLUGS[m[1]]) {
    clean = `/product/${LEGACY_PRODUCT_SLUGS[m[1]]}`;
  }
  // Legacy WooCommerce taxonomy URLs (/product-tag/*, /product-category/*) → homepage
  if (/^\/(product-tag|product-category|shop)(\/|$)/i.test(clean)) {
    clean = "/";
  }
  // Drop noisy WooCommerce query params
  const params = new URLSearchParams(search);
  ["add-to-cart", "wc-ajax", "removed_item", "lang"].forEach((k) => params.delete(k));
  const cleanSearch = params.toString() ? `?${params.toString()}` : "";
  if (clean === pathname && cleanSearch === search) {
    // Nothing to redirect — render NotFound.
    return <NotFound />;
  }
  return <Navigate to={clean + cleanSearch} replace />;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Analytics />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/product/:handle" element={<ProductDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/refunds" element={<RefundPolicy />} />
              <Route path="/shipping" element={<ShippingPolicy />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/subscriptions" element={<SubscriptionsManage />} />
              <Route path="/account/orders/:id" element={<OrderTracking />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/subscribe/:handle" element={<SubscribeCheckout />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/thekesarkitchen" element={<Journal />} />
              <Route path="/thekesarkitchen/topic/:tag" element={<JournalTopic />} />
              <Route path="/thekesarkitchen/:slug" element={<JournalPost />} />
              <Route path="/journal/*" element={<JournalRedirect />} />
              <Route path="/thekitchen/*" element={<OldKitchenRedirect />} />
              <Route path="/author/:slug" element={<Author />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/wholesale" element={<Wholesale />} />
              <Route path="/wholesale-saffron" element={<Navigate to="/wholesale" replace />} />
              <Route path="/wholesale-saffron/" element={<Navigate to="/wholesale" replace />} />
              <Route path="/wholesale/account" element={<WholesaleAccount />} />
              <Route path="/watch/:slug" element={<Watch />} />
              <Route path="/kashmiri-chai" element={<KashmiriChai />} />
              <Route path="/saffron-guide" element={<SaffronGuide />} />
              <Route path="/kahwa" element={<KahwaGuide />} />
              <Route path="/compare/:slug" element={<Compare />} />
              <Route path="/why-saffron" element={<WhySaffron />} />
              {/* Catch-all: handle legacy WP/WooCommerce URLs (locale prefixes, old slugs,
                  add-to-cart params, trailing slashes) by redirecting; otherwise render NotFound. */}
              <Route path="*" element={<LegacyUrlRedirect />} />
            </Routes>
          </Suspense>
          <CookieBanner />
          <InstallPrompt />
          <SignupPopup />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
